/**
 * Write operations. Everything that changes data goes through here so the
 * invariants live in one place: prices are snapshotted, totals are computed,
 * and member + subscription + payment are written atomically or not at all.
 *
 * Every write ends by nudging the sync engine, so a change reaches the cloud
 * within about a second of being made rather than waiting for the next tick.
 */
import {
  db,
  getSharedSetting,
  getSharedSettingNumber,
  markDeleted,
  newRowFields,
  setSharedSetting,
  SHARED_SETTING_KEYS,
  touch,
} from './db';
import {
  extendEndDate,
  freezeEndDate,
  validateFreeze,
  withdrawEndDate,
} from '@shared/freezes';
import { allocateSkId } from './ids';
import { computeEndDate, todayISO, isValidISODate } from '@shared/dates';
import {
  quoteCancellation,
  validateCancellation,
  type CancellationQuote,
} from '@shared/cancellations';
import {
  computeAmountPaid,
  computeTotalDue,
  DEFAULT_WALK_IN_PRICE_PIASTRES,
  defaultPriceFor,
  priceKey,
  TERM_MONTHS,
  type AgeGroup,
  type MembershipType,
  type TermMonths,
  type TrainingMode,
} from '@shared/pricing';
import { normalizePhone } from '@shared/identity';
import {
  DEFAULT_SPLIT,
  exceptionsOnly,
  parseCoachRates,
  serialiseCoachRates,
  type CoachRates,
} from '@shared/payouts';
import { findClass } from '@shared/catalog';
import { requestSync } from '../sync/engine';
import type {
  Freeze,
  ISODate,
  Member,
  Payment,
  PaymentMethod,
  Subscription,
  WalkIn,
} from '@shared/types';

export interface NewSubscriptionInput {
  /** Set to attach the subscription to an existing member instead of creating one. */
  existingMemberId?: string;
  fullName: string;
  phone: string;
  notes?: string;
  /** Club house members pay the reduced rate. */
  clubMember: boolean;
  classKey: string;
  /** One-to-one training. */
  private: boolean;
  /**
   * Who runs the one-to-one sessions. Required when `private` is set, ignored
   * otherwise — see `private_coach` on Subscription for why it is a name.
   */
  privateCoach?: string;
  /** 1, 3 or 6 — the only terms sold. */
  months: number;
  startDate: ISODate;
  /** The TOTAL for the term, looked up from the schedule and snapshotted here. */
  pricePiastres: number;
  discountPiastres: number;
  /** 0 is valid — a member may pay later. */
  amountPaidNowPiastres: number;
  paymentMethod: PaymentMethod;
  receivedBy: string;
  /**
   * Front desk has seen that a live member already has this exact name AND
   * phone and says this is a different person. Without it, creating a new
   * member that matches both is refused — see `createSubscription`.
   */
  confirmedDifferentPerson?: boolean;
}

export interface NewSubscriptionResult {
  member: Member;
  subscription: Subscription;
  payment: Payment | null;
  createdMember: boolean;
}

/**
 * A name reduced to what a person would call "the same": case, spacing and the
 * Arabic spellings typists swap freely (hamza forms of alef, teh marbuta vs heh,
 * alef maksura vs yeh, tatweel and short-vowel marks) all fold away.
 */
export function normalizeName(name: string): string {
  return name
    .replace(/[\u064B-\u0652\u0640]/g, '') // short-vowel marks, tatweel
    .replace(/[\u0623\u0625\u0622]/g, '\u0627') // alef with hamza / madda -> alef
    .replace(/\u0629/g, '\u0647') // teh marbuta -> heh
    .replace(/\u0649/g, '\u064A') // alef maksura -> yeh
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();
}

/** Field-level validation, pure and testable. */
export function validateNewSubscription(
  input: Partial<NewSubscriptionInput>
): Record<string, string> {
  const errors: Record<string, string> = {};

  if (!input.existingMemberId && !input.fullName?.trim()) {
    errors.fullName = 'Enter the member’s name';
  }
  if (!input.classKey) errors.classKey = 'Choose a class';
  else if (!findClass(input.classKey)) errors.classKey = 'Unknown class';

  // A private hour is not automatically the class coach's — someone has to say
  // who is running it, or the payout report cannot attribute the money.
  if (input.private && !input.privateCoach?.trim()) {
    errors.privateCoach = 'Choose who is taking the private sessions';
  }

  const months = input.months ?? 0;
  if (!TERM_MONTHS.includes(months as TermMonths)) {
    errors.months = 'Choose 1, 3 or 6 months';
  }

  if (!input.startDate || !isValidISODate(input.startDate)) {
    errors.startDate = 'Enter a valid start date';
  }

  const price = input.pricePiastres ?? 0;
  if (!Number.isInteger(price) || price < 0) errors.price = 'Invalid price';

  const discount = input.discountPiastres ?? 0;
  if (!Number.isInteger(discount) || discount < 0) {
    errors.discount = 'Discount cannot be negative';
  } else if (discount > price) {
    errors.discount = 'Discount is more than the price';
  }

  const paid = input.amountPaidNowPiastres ?? 0;
  if (!Number.isInteger(paid) || paid < 0) {
    errors.amountPaid = 'Invalid amount';
  } else if (paid > computeTotalDue(price, discount)) {
    errors.amountPaid = 'Paid is more than the total due';
  }

  return errors;
}

/**
 * The core write of the whole system.
 *
 * Member, subscription and payment go into ONE Dexie transaction. If the tab
 * closes or the machine loses power halfway through, IndexedDB rolls the whole
 * thing back — there is no state where a member exists with no subscription,
 * or a payment points at a subscription that was never written.
 */
export async function createSubscription(
  input: NewSubscriptionInput
): Promise<NewSubscriptionResult> {
  const errors = validateNewSubscription(input);
  const firstError = Object.values(errors)[0];
  if (firstError) throw new Error(firstError);

  const result = await db.transaction(
    'rw',
    [db.members, db.subscriptions, db.payments, db.settings],
    async () => {
      let member: Member;
      let createdMember = false;

      if (input.existingMemberId) {
        const existing = await db.members.get(input.existingMemberId);
        if (!existing) throw new Error('That member no longer exists');
        // Club membership can change; keep the member record current.
        if (existing.club_member !== input.clubMember) {
          existing.club_member = input.clubMember;
          await db.members.put(touch(existing));
        }
        member = existing;
      } else {
        // Same name AND same phone is the one person registered twice, not a
        // sibling on a parent's number (which has a different name). The UI
        // warns, but staff clicked through it once, so the write refuses too.
        if (!input.confirmedDifferentPerson) {
          const sameName = (await findMembersByPhone(input.phone)).find(
            (m) => normalizeName(m.full_name) === normalizeName(input.fullName)
          );
          if (sameName) {
            throw new Error(
              `${sameName.sk_id} ${sameName.full_name} is already registered with this name and phone. Use the existing member.`
            );
          }
        }
        member = {
          ...newRowFields(),
          sk_id: await allocateSkId(),
          full_name: input.fullName.trim(),
          phone: normalizePhone(input.phone),
          club_member: input.clubMember,
          notes: input.notes?.trim() ?? '',
        };
        await db.members.add(member);
        createdMember = true;
      }

      const subscription: Subscription = {
        ...newRowFields(),
        member_id: member.id,
        class_key: input.classKey,
        private: input.private,
        // Snapshotted like the price: renaming a coach later must not
        // re-attribute revenue that has already been paid out.
        private_coach: input.private ? (input.privateCoach?.trim() ?? '') : '',
        // Snapshotted alongside the price: if they join the club house next
        // month, this sale must still show the rate they actually paid.
        club_member: input.clubMember,
        start_date: input.startDate,
        months: input.months,
        end_date: computeEndDate(input.startDate, input.months),
        price_piastres: input.pricePiastres,
        discount_piastres: input.discountPiastres,
        total_due_piastres: computeTotalDue(input.pricePiastres, input.discountPiastres),
        cancelled: false,
        cancelled_at: null,
        sessions_attended: null,
        refund_piastres: null,
      };
      await db.subscriptions.add(subscription);

      let payment: Payment | null = null;
      if (input.amountPaidNowPiastres > 0) {
        payment = {
          ...newRowFields(),
          subscription_id: subscription.id,
          member_id: member.id,
          amount_piastres: input.amountPaidNowPiastres,
          method: input.paymentMethod,
          paid_at: todayISO(),
          received_by: input.receivedBy,
        };
        await db.payments.add(payment);
      }

      return { member, subscription, payment, createdMember };
    }
  );

  requestSync();
  return result;
}

/**
 * Record a payment against an existing subscription. Append-only — this never
 * edits a previous row, which is why payments cannot conflict during sync.
 */
export async function recordPayment(args: {
  subscriptionId: string;
  memberId: string;
  amountPiastres: number;
  method: PaymentMethod;
  receivedBy: string;
}): Promise<Payment> {
  if (!Number.isInteger(args.amountPiastres) || args.amountPiastres === 0) {
    throw new Error('Enter a payment amount');
  }

  const payment: Payment = {
    ...newRowFields(),
    subscription_id: args.subscriptionId,
    member_id: args.memberId,
    amount_piastres: args.amountPiastres,
    method: args.method,
    paid_at: todayISO(),
    received_by: args.receivedBy,
  };
  await db.payments.add(payment);
  requestSync();
  return payment;
}

/* -------------------------------------------------------------------------
   Cancellation
   ------------------------------------------------------------------------- */

export interface CancellationResult {
  subscription: Subscription;
  /** The negative payment row, or null when there was nothing to give back. */
  refund: Payment | null;
  quote: CancellationQuote;
}

/**
 * Cancel a subscription and hand back the unused part of the bundle.
 *
 * Only inside the 14-day window from the start date — after that the term is
 * the member's and this throws rather than silently refunding nothing.
 *
 * The refund is written as a NEGATIVE PAYMENT, not as an edit to anything.
 * Payments are append-only, and every revenue figure in the system — the
 * dashboard, both portals, the payout report, each coach's share — is a sum
 * over that table. A negative row therefore reduces all of them at once, in
 * the period the refund was actually given, with no report needing to know
 * that cancellations exist.
 *
 * The subscription flag and the refund row go in ONE transaction: a cancelled
 * subscription with no refund would quietly keep the member's money, and a
 * refund with no cancellation would pay them while leaving them subscribed.
 */
export async function cancelSubscription(args: {
  subscriptionId: string;
  sessionsAttended: number;
  receivedBy: string;
  today?: ISODate;
}): Promise<CancellationResult> {
  const today = args.today ?? todayISO();

  const result = await db.transaction(
    'rw',
    [db.subscriptions, db.payments],
    async () => {
      const subscription = await db.subscriptions.get(args.subscriptionId);
      if (!subscription || subscription.deleted_at !== null) {
        throw new Error('That subscription no longer exists');
      }

      const errors = validateCancellation(subscription, args.sessionsAttended, today);
      const firstError = Object.values(errors)[0];
      if (firstError) throw new Error(firstError);

      const paid = computeAmountPaid(
        await db.payments.where('subscription_id').equals(subscription.id).toArray()
      );
      const quote = quoteCancellation(subscription, args.sessionsAttended, paid);

      let refund: Payment | null = null;
      if (quote.refund > 0) {
        refund = {
          ...newRowFields(),
          subscription_id: subscription.id,
          member_id: subscription.member_id,
          // Negative: money leaving the till, in the append-only ledger.
          amount_piastres: -quote.refund,
          method: 'cash',
          paid_at: today,
          received_by: args.receivedBy,
        };
        await db.payments.add(refund);
      }

      subscription.cancelled = true;
      subscription.cancelled_at = today;
      subscription.sessions_attended = quote.sessionsAttended;
      subscription.refund_piastres = quote.refund;
      await db.subscriptions.put(touch(subscription));

      return { subscription, refund, quote };
    }
  );

  requestSync();
  return result;
}

/**
 * What a cancellation would cost right now, without writing anything.
 * The dialog calls this on every keystroke so staff see the refund before
 * they commit to it.
 */
export async function previewCancellation(
  subscriptionId: string,
  sessionsAttended: number
): Promise<CancellationQuote | null> {
  const subscription = await db.subscriptions.get(subscriptionId);
  if (!subscription || subscription.deleted_at !== null) return null;

  const paid = computeAmountPaid(
    await db.payments.where('subscription_id').equals(subscriptionId).toArray()
  );
  return quoteCancellation(subscription, sessionsAttended, paid);
}

/* -------------------------------------------------------------------------
   Voids
   ------------------------------------------------------------------------- */

/**
 * Void a subscription that should never have existed.
 *
 * NOT a cancellation. A cancellation says the member joined and then stopped,
 * so it refunds the unused part and leaves a negative payment behind as the
 * record of cash crossing the desk. A void says the sale never happened at
 * all — wrong class, duplicate member, typed onto the wrong record — and
 * writes no payment of any kind, because a matched pair of rows describing
 * money that never moved is exactly what makes a till stop reconciling.
 *
 * The subscription is soft-deleted and its payments stop counting everywhere;
 * `dropVoidedPayments` in shared/voids.ts is what makes that true. The payment
 * rows themselves are untouched, so `payments` keeps having no UPDATE and no
 * DELETE policy for anybody, and the void stays reversible.
 *
 * Gated on the manager PIN at the call site rather than in here — this
 * function is the write, not the authorisation.
 */
export async function voidSubscription(subscriptionId: string): Promise<Subscription> {
  const subscription = await db.transaction('rw', [db.subscriptions], async () => {
    const found = await db.subscriptions.get(subscriptionId);
    if (!found || found.deleted_at !== null) {
      throw new Error('That subscription no longer exists');
    }
    const voided = markDeleted(found);
    await db.subscriptions.put(voided);
    return voided;
  });

  requestSync();
  return subscription;
}

/**
 * Void a walk-in recorded by mistake.
 *
 * Simpler than voiding a subscription: a drop-in carries its own money on the
 * row rather than in `payments`, so there is nothing left behind to filter out
 * — every revenue and payout path already skips a soft-deleted walk-in. The
 * money leaves the takings and the class coach's share the moment this lands.
 *
 * Same rule as a subscription void, though: this is for a drop-in that was
 * never taken. If the person paid and wants their money back, that is cash out
 * of the till and belongs in a record of it, not in a row that disappears.
 */
export async function voidWalkIn(walkInId: string): Promise<WalkIn> {
  const walkIn = await db.transaction('rw', [db.walk_ins], async () => {
    const found = await db.walk_ins.get(walkInId);
    if (!found || found.deleted_at !== null) {
      throw new Error('That walk-in no longer exists');
    }
    const voided = markDeleted(found);
    await db.walk_ins.put(voided);
    return voided;
  });

  requestSync();
  return walkIn;
}

/* -------------------------------------------------------------------------
   Freezes
   ------------------------------------------------------------------------- */

/**
 * Pause a subscription.
 *
 * The freeze row and the extended end date are written in ONE transaction. If
 * only one landed, the member would either lose the days they paused or gain
 * days nobody recorded — both are the kind of error that surfaces months later
 * as an argument at the desk.
 */
export async function createFreeze(args: {
  subscriptionId: string;
  startDate: ISODate;
  days: number;
  reason?: string;
  createdBy: string;
}): Promise<Freeze> {
  const result = await db.transaction('rw', [db.subscriptions, db.freezes], async () => {
    const subscription = await db.subscriptions.get(args.subscriptionId);
    if (!subscription || subscription.deleted_at !== null) {
      throw new Error('That subscription no longer exists');
    }

    const existing = await db.freezes
      .where('subscription_id')
      .equals(args.subscriptionId)
      .toArray();

    const errors = validateFreeze(
      subscription,
      existing,
      args.startDate,
      args.days,
      todayISO()
    );
    const firstError = Object.values(errors)[0];
    if (firstError) throw new Error(firstError);

    const freeze: Freeze = {
      ...newRowFields(),
      subscription_id: subscription.id,
      member_id: subscription.member_id,
      start_date: args.startDate,
      end_date: freezeEndDate(args.startDate, args.days),
      days: args.days,
      reason: args.reason?.trim() ?? '',
      created_by: args.createdBy,
    };
    await db.freezes.add(freeze);

    // The member gets back exactly the days they paused.
    subscription.end_date = extendEndDate(subscription.end_date, args.days);
    await db.subscriptions.put(touch(subscription));

    return freeze;
  });

  requestSync();
  return result;
}

/**
 * Cancel a freeze, pulling the extension back off the end date.
 *
 * Soft-deletes the row rather than removing it, so the record of what happened
 * survives even though its effect is undone.
 */
export async function cancelFreeze(freezeId: string): Promise<void> {
  await db.transaction('rw', [db.subscriptions, db.freezes], async () => {
    const freeze = await db.freezes.get(freezeId);
    if (!freeze || freeze.deleted_at !== null) return;

    const subscription = await db.subscriptions.get(freeze.subscription_id);
    if (subscription && subscription.deleted_at === null) {
      subscription.end_date = withdrawEndDate(subscription.end_date, freeze.days);
      await db.subscriptions.put(touch(subscription));
    }

    await db.freezes.put(markDeleted(freeze));
  });

  requestSync();
}

/** Every freeze on a subscription, including cancelled ones for the audit trail. */
export async function getFreezesForSubscription(subscriptionId: string): Promise<Freeze[]> {
  return db.freezes.where('subscription_id').equals(subscriptionId).toArray();
}

/* -------------------------------------------------------------------------
   Walk-ins
   ------------------------------------------------------------------------- */

export function validateWalkIn(args: {
  classKey?: string;
  personName?: string;
  phone?: string;
}): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!args.classKey) errors.classKey = 'Choose a class';
  else if (!findClass(args.classKey)) errors.classKey = 'Unknown class';

  // Name and phone are required: a drop-in is still a person on the mat, and
  // the academy needs a record of who was in the building.
  if (!args.personName?.trim()) errors.personName = 'Enter their name';
  if (normalizePhone(args.phone ?? '').length < 8) errors.phone = 'Enter a valid phone number';

  return errors;
}

/**
 * Record a drop-in session. One flat price for any class.
 *
 * No SK ID and no member row: walk-ins are frequently one-time visitors, and
 * minting an ID for each would bury real members in the list.
 */
export async function recordWalkIn(args: {
  classKey: string;
  personName: string;
  phone: string;
  amountPiastres: number;
  method: PaymentMethod;
  receivedBy: string;
}): Promise<WalkIn> {
  const errors = validateWalkIn(args);
  const firstError = Object.values(errors)[0];
  if (firstError) throw new Error(firstError);

  if (!Number.isInteger(args.amountPiastres) || args.amountPiastres < 0) {
    throw new Error('Invalid walk-in amount');
  }

  const walkIn: WalkIn = {
    ...newRowFields(),
    class_key: args.classKey,
    person_name: args.personName.trim(),
    phone: normalizePhone(args.phone),
    // Snapshotted, so changing the walk-in price never rewrites past takings.
    amount_piastres: args.amountPiastres,
    method: args.method,
    visited_at: todayISO(),
    received_by: args.receivedBy,
  };
  await db.walk_ins.add(walkIn);
  requestSync();
  return walkIn;
}

export async function getWalkInPrice(): Promise<number> {
  return getSharedSettingNumber(
    SHARED_SETTING_KEYS.walkInPrice,
    DEFAULT_WALK_IN_PRICE_PIASTRES
  );
}

export async function setWalkInPrice(pricePiastres: number): Promise<void> {
  await setSharedSetting(SHARED_SETTING_KEYS.walkInPrice, String(pricePiastres));
  requestSync();
}

/** The payout split, read from the synced settings so the portal agrees. */
export async function getSplit(): Promise<{
  coachPercent: number;
  managerPercent: number;
  cityViewPercent: number;
}> {
  const [coachPercent, managerPercent, cityViewPercent] = await Promise.all([
    getSharedSettingNumber(SHARED_SETTING_KEYS.coachPercent, DEFAULT_SPLIT.coachPercent),
    getSharedSettingNumber(SHARED_SETTING_KEYS.managerPercent, DEFAULT_SPLIT.managerPercent),
    getSharedSettingNumber(SHARED_SETTING_KEYS.cityViewPercent, DEFAULT_SPLIT.cityViewPercent),
  ]);
  return { coachPercent, managerPercent, cityViewPercent };
}

export async function setSplit(split: {
  coachPercent: number;
  managerPercent: number;
  cityViewPercent: number;
}): Promise<void> {
  await setSharedSetting(SHARED_SETTING_KEYS.coachPercent, String(split.coachPercent));
  await setSharedSetting(SHARED_SETTING_KEYS.managerPercent, String(split.managerPercent));
  await setSharedSetting(SHARED_SETTING_KEYS.cityViewPercent, String(split.cityViewPercent));
  requestSync();
}

/**
 * The coaches who are NOT on the default percentage.
 *
 * Everyone absent from this map earns `coach_percent`, so the common case is an
 * empty object. The extra a named coach takes comes out of the manager's share,
 * never City View's — see `ratesFor` in shared/payouts.
 */
export async function getCoachRates(): Promise<CoachRates> {
  return parseCoachRates(await getSharedSetting(SHARED_SETTING_KEYS.coachRates));
}

/**
 * Replace the whole map. Rates equal to the default are dropped rather than
 * stored, so "everyone else is on 40%" stays true after the default changes —
 * a stored 40 would silently pin that coach when the default moved to 45.
 */
export async function setCoachRates(
  rates: CoachRates,
  defaultPercent: number
): Promise<void> {
  await setSharedSetting(
    SHARED_SETTING_KEYS.coachRates,
    serialiseCoachRates(exceptionsOnly(rates, defaultPercent))
  );
  requestSync();
}

/* -------------------------------------------------------------------------
   Price schedule
   ------------------------------------------------------------------------- */

/**
 * The scheduled TOTAL for a term. Falls back to the built-in City View
 * schedule if the row is missing, so a sale can never be blocked by a gap.
 */
export async function getScheduledPrice(
  ageGroup: AgeGroup,
  mode: TrainingMode,
  membership: MembershipType,
  months: TermMonths
): Promise<number> {
  const key = priceKey(ageGroup, mode, membership, months);
  const row = await db.price_schedule.where('price_key').equals(key).first();
  if (!row || row.deleted_at !== null) {
    return defaultPriceFor(ageGroup, mode, membership, months);
  }
  return row.price_piastres;
}

export async function setScheduledPrice(key: string, pricePiastres: number): Promise<void> {
  const row = await db.price_schedule.where('price_key').equals(key).first();
  if (!row) {
    await db.price_schedule.add({
      ...newRowFields(),
      price_key: key,
      price_piastres: pricePiastres,
    });
  } else {
    row.price_piastres = pricePiastres;
    await db.price_schedule.put(touch(row));
  }
  requestSync();
}

/* -------------------------------------------------------------------------
   Coach assignments
   ------------------------------------------------------------------------- */

export async function setCoachForClass(classKey: string, coachName: string): Promise<void> {
  const existing = await db.coach_assignments.where('class_key').equals(classKey).first();
  const name = coachName.trim();

  if (!existing) {
    if (!name) return;
    await db.coach_assignments.add({ ...newRowFields(), class_key: classKey, coach_name: name });
  } else {
    existing.coach_name = name;
    await db.coach_assignments.put(touch(existing));
  }
  requestSync();
}

/** class_key -> coach name, for the payout report. */
export async function getCoachAssignments(): Promise<Record<string, string>> {
  const rows = await db.coach_assignments.toArray();
  const out: Record<string, string> = {};
  for (const r of rows) {
    if (r.deleted_at === null && r.coach_name.trim()) out[r.class_key] = r.coach_name.trim();
  }
  return out;
}

/* -------------------------------------------------------------------------
   Coach roster — the people who are not tied to a class
   ------------------------------------------------------------------------- */

/**
 * Add a coach to the roster.
 *
 * Idempotent on the name, because the same name is the same person: adding
 * "Karim" twice must not create a second row that would then collide with the
 * partial unique index in Postgres and abort the whole sync run. A name that
 * was soft-deleted and re-added is revived rather than duplicated.
 */
export async function addCoach(name: string): Promise<void> {
  const trimmed = name.trim();
  if (!trimmed) throw new Error('Enter a coach name');

  const existing = (await db.coaches.toArray()).find(
    (c) => c.name.trim().toLowerCase() === trimmed.toLowerCase()
  );

  if (!existing) {
    await db.coaches.add({ ...newRowFields(), name: trimmed });
  } else if (existing.deleted_at !== null) {
    existing.deleted_at = null;
    existing.name = trimmed;
    await db.coaches.put(touch(existing));
  } else {
    return; // Already on the roster.
  }

  requestSync();
}

/**
 * Take a coach off the roster. Soft delete, like everything else.
 *
 * Subscriptions that name them keep the name — it was snapshotted at the sale,
 * so past payouts stay exactly as they were reported. Removing someone stops
 * them being offered for NEW privates; it does not rewrite history.
 */
export async function removeCoach(coachId: string): Promise<void> {
  const row = await db.coaches.get(coachId);
  if (!row || row.deleted_at !== null) return;
  await db.coaches.put(markDeleted(row));
  requestSync();
}

/** Every coach on the roster, alphabetically. Excludes soft-deleted rows. */
export async function getRoster(): Promise<string[]> {
  const rows = await db.coaches.toArray();
  return rows
    .filter((c) => c.deleted_at === null && c.name.trim())
    .map((c) => c.name.trim())
    .sort((a, b) => a.localeCompare(b, ['ar', 'en']));
}

/**
 * Everyone who can be put on a private subscription: the class coaches and the
 * roster together, de-duplicated.
 *
 * The two lists overlap by design — a coach who runs a class may well also run
 * privates, and they should appear once.
 */
export async function getSelectableCoaches(): Promise<string[]> {
  const [assignments, roster] = await Promise.all([getCoachAssignments(), getRoster()]);
  const names = new Set<string>([...Object.values(assignments), ...roster]);
  return [...names]
    .map((n) => n.trim())
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b, ['ar', 'en']));
}

/* -------------------------------------------------------------------------
   Reads used by the new-subscription flow
   ------------------------------------------------------------------------- */

/** Live members sharing a phone number — powers the duplicate warning. */
export async function findMembersByPhone(phone: string): Promise<Member[]> {
  const normalized = normalizePhone(phone);
  if (normalized.length < 6) return [];
  const matches = await db.members.where('phone').equals(normalized).toArray();
  return matches.filter((m) => m.deleted_at === null);
}
